import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
    Registration, EstimatedCosts, OfferType, Proposal, Product
} from '@enus/model/registration';
import { RegistrationServiceBase } from '@common/services/registration.service.base';
import { API_URL, BUSINESS } from '@common/app/app.config';
import { CommunityService } from '@common/services/community.service';
import { AuthService } from '@common/services/auth.service';
import { Observable, from, throwError, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { LanguageService } from '@common/services/language.service';
import { AssignToCommunityResult } from '@country/us/model/http-results';

@Injectable()
export class RegistrationService extends RegistrationServiceBase<Registration> {

    constructor(@Inject(BUSINESS) business,
        @Inject(API_URL) baseUrl: string,
        http: HttpClient,
        communityService: CommunityService,
        auth: AuthService,
        private languageService: LanguageService,
        private router: Router) {

        super(business, http, baseUrl, communityService, auth);
        this.participantMinimum = 1000;
    }

    getEstimatedCosts$(registrationId: string, offerType: OfferType): Observable<EstimatedCosts> {
        return this.http.get<EstimatedCosts>(this.url + `/${registrationId}/get-estimated-costs?offerType=${offerType}`);
    }

    revertDecision(id: string): Observable<void> {
        return this.http.post<void>(this.url + '/' + id + '/revert-decision-as-user', null);
    }

    getAllowedUtilities(): string[] {
        return ['centerpoint', 'oncor', 'aep-texas-central', 'tnmp'];
    }

    getDisallowedUtilities(): string[] {
        return ['aep-texas-north', 'sharyland', 'sharyland-mcallen'];
    }

    assignToCommunity(id: string, targetCommunityCode: string): Observable<AssignToCommunityResult> {
        return this.http.post<AssignToCommunityResult>(this.url + `/${id}/assign-to-community?targetCommunityCode=${targetCommunityCode}`, null);
    }

    getDefaultProductValues$(): Observable<Product> {
        return this.http.get<Product>(this.url + `/get-default-product-data`);
    }

    getDefaultProposal$(utility: string, auctionCode: string): Observable<Proposal> {
        return this.http.get<Proposal>(this.url + `/get-default-proposal?utility=${utility}&auctionCode=${auctionCode}`);
    }

    getDefaultEstimatedKwhUsage(): number {
        return 0;
    }

    countSubscriptions(auctionId: string): Observable<number> {
        return this.http.get<number>(this.url + '/count/all-subscriptions-without-rollovers');
    }

    count(communityId?: string): Observable<number> {
        return this.http.get<number>(this.url + '/count/all-subscriptions-without-rollovers');
    }

    addMinimalRegistration(registration: Registration): Observable<string> {
        return this.addRegistration(`${this.url}/minimal-registration/`, registration);
    }

    updateMinimalRegistration(registration: Registration): Observable<void> {
        return this.http.put<void>(`${this.url}/minimal-registration/${registration.id}`, registration);
    }

    checkMinimalRegistrationExists(registration: Registration): Observable<string> {
        return this.http.post<string>(`${this.url}/check-minimal-registration-exists/`, registration);
    }

    sendLinkToRegistration(registrationId: string): Observable<void> {
        const url = `${this.url}/${registrationId}/send-participant-link`;
        return this.http.post<void>(url, {});
    }

    add(registration: Registration): Observable<string> {
        return this.addRegistration(this.url, registration);
    }

    get(id: string): Observable<Registration> {
        return this.http
            .get<Registration>(`${this.url}/${id}`)
            .pipe(
                catchError(e => this.catchErrorOnGet(id, e))
            );
    }

    private addRegistration(url: string, registration: Registration): Observable<string> {
        return this.http.post<string>(url, registration, { observe: 'response' })
            .pipe(switchMap(response => {
                const registrationId = response.body;
                return this.auth.loginAsParticipant(response)
                    .pipe(switchMap(_ => of(registrationId)));
            }));
    }

    private catchErrorOnGet(registrationId: string, e: any): Observable<Registration> | Observable<never> {
        const unauthorizedStatuses = [401, 403, 404];
        if (unauthorizedStatuses.find((status) => status === e?.status)) {
            return this.communityService.communityCode$.pipe(
                take(1),
                mergeMap((communityCode) =>
                    from(
                        this.router.navigate([
                            communityCode,
                            this.languageService.currentLanguageCode,
                            'unauthorized',
                            registrationId
                        ]),
                    )
                ),
                map(() => new Registration())
            );
        }
        return throwError(e);
    }
}
